import React, { useCallback, useEffect, useState } from 'react'
import {
    engine,
    getDriverId,
    getMainStationId,
    getChatUserId,
    getChatCompanyId,
    showErrorToast,
    showToast
} from "../../Utilities";
import { useLoading } from '../../Chat/Loading';
import { useTrackedState, useUpdate as useBotDispatch } from './Store';
import BotMessage from './BotMessage';
import BotInput from './BotInput';
import { ScrollContainer } from './BotTaskList';
import moment from 'moment';

function BotReviewMileage({ taskInformation, channel, setOPenReviewModal }) {
    const state = useTrackedState();
    const { message } = state;
    const dispatch = useBotDispatch();

    const { setLoading } = useLoading();
    const [task, setTask] = useState();
    const [placeholder, setPlaceholder] = useState("Please enter correct mileage....");
    const [disabled, setDisabled] = useState(true);
    const [submitButtonText, setSubmitButtonText] = useState("Save");
    const [taskCompleted, setTaskCompleted] = useState(false);
    const [disableButton, setDisableButton] = useState(false);

    useEffect(() => {
        const abortController = new AbortController();

        fetchMileageReviewTask(abortController.signal).then(data => {
            if (abortController.signal.aborted) return;
            if (data) {
                setTask(data.task);
                if (data?.message && data?.message?.length > 0) {
                    setDisabled(false);
                }
                setLoading(false);
            }
        });

        return () => {
            abortController.abort();
        };
    }, []);

    async function fetchMileageReviewTask(signal) {
        try {
            let { data } = await engine({ hideDefaultMessage: true })
                .post("/api/fetch_task", {
                    task: {
                        id: taskInformation.id,
                        type: taskInformation.type,
                    },
                    companyId: getChatCompanyId(),
                    stationId: getMainStationId(),
                    userId: getChatUserId(),
                    driverId: getDriverId(),
                    datetime: moment().toISOString(),
                    messageSid: message?.id,
                    channelSid: channel?.id,
                    attributes: message?.attributes,
                    taskInformation,
                }, { signal });

            if (data?.success === false && data?.message === "This task was already closed by another user.") {
                await showToast({
                    type: 'success',
                    title: 'Task is Archived',
                    content: data?.message
                });
                setOPenReviewModal(false);
                return false;
            }
            return data;
        } catch (e) {
            if (e?.name === 'AbortError' || e?.name === 'CanceledError') return null;
            await showErrorToast(e, 'Error in fetching the task information for mileage review.');
            return null;
        }
    }

    const removeMessage = useCallback(
        async () => {
            dispatch({ type: "MESSAGE_REMOVED", sid: message.id });
        },
        [dispatch, message],
    );

    const completeTask = useCallback(
        async ({ mileage }) => {
            try {
                setDisableButton(true);
                let params = {
                    datetime: moment().toISOString(),
                    task,
                    stationId: getMainStationId(),
                    userId: getChatUserId(),
                    driverId: getDriverId(),
                    mileage: mileage
                };
                let response = await engine().post('/api/complete_task', params);
                if (response.data?.success === false && response.data?.message === "This task was already closed by another user.") {
                    await showToast({
                        type: 'success',
                        title: 'Task is Archived',
                        content: response.data?.message
                    });
                    setOPenReviewModal(false);
                    return;
                }
                if (response.data?.success === false) {
                    await showErrorToast(null, response.data?.message || 'Failed to complete mileage review task.');
                    setDisableButton(false);
                    return;
                }
                setTaskCompleted(true);
                await removeMessage();
            } catch (e) {
                setDisableButton(false);
                await showErrorToast(e, 'Error in mileage review task completion.');
            }
        },
        [removeMessage, task, setOPenReviewModal],
    );

    const submitMessage = useCallback(
        async (mileage) => {
            const numericMileage = parseFloat(mileage);
            if (isNaN(numericMileage) || numericMileage < 0) {
                await showErrorToast(null, 'Please enter a valid mileage value.');
                return;
            }
            try {
                setPlaceholder("");
                setSubmitButtonText("Send");
                await completeTask({ mileage: numericMileage });
            } catch (e) {
                await showErrorToast(e, 'Error submitting mileage.');
            }
        },
        [completeTask],
    );

    return (
        <>
            <ScrollContainer mode={'bottom'} height={"calc(100% - 156px) !important"}>
                <BotMessage currentMessage={message} />
                {taskCompleted &&
                    <BotMessage currentMessage={{
                        ...message,
                        date: new Date(),
                        text: "Task Completed!",
                    }} />
                }
            </ScrollContainer>
            <BotInput submitMessage={submitMessage} placeholder={placeholder} disableSendButton={disableButton} submitButtonText={submitButtonText} disabled={disabled} />
        </>
    )
}

export default BotReviewMileage
